import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Badge, Pagination, Modal } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { AdminImageDetailDto, PageDto } from '../types';

const ITEMS_PER_PAGE = 12;

interface ImageFilters {
    doctor: string;
    dateStart: string;
    dateEnd: string;
    diagnosis: string;
    contour: string;
}

const EMPTY_FILTERS: ImageFilters = {
    doctor: '',
    dateStart: '',
    dateEnd: '',
    diagnosis: 'all',
    contour: 'all',
};

// Modal content type
type ModalContentType = 'diagnosis' | 'prompt' | null;

// Result of POST /admin/cleanup/stage-images
interface StageImageCleanupResult {
    reportedAnalyses: number;
    imagesDeleted: number;
    analysesMissing: number;
}

const getDiagnosisPreview = (value?: string): string => {
    if (!value) return '';
    const trimmed = value.trim();
    const withoutFence = trimmed
        .replace(/^```(?:json)?\s*/i, '')
        .replace(/```$/i, '')
        .trim();

    try {
        const parsed = JSON.parse(withoutFence);
        const candidate = [
            parsed.conclusions,
            parsed.provisional_diagnosis,
            parsed.report_text,
            parsed.recommendations,
            parsed.image_description,
        ].find((item) => typeof item === 'string' && item.trim().length > 0);
        if (candidate) return candidate.trim();
    } catch {
        // Keep plain text diagnoses as-is.
    }

    return withoutFence.split('\n').find((line) => line.trim().length > 0)?.trim() || 'Есть диагноз';
};

const truncate = (value: string, maxLength = 70): string =>
    value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;

const formatImageDate = (value?: string): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.replace('T', ' ').slice(0, 16);
    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

// Component to handle protected image loading
const ProtectedImage: React.FC<{ imageId: string, alt: string }> = ({ imageId, alt }) => {
    const [src, setSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let objectUrl: string | null = null;
        const fetchImage = async () => {
            try {
                // Use the protected endpoint with the existing axios instance (which has auth interceptor)
                const response = await api.get(`/images/file/${imageId}`, { responseType: 'blob' });
                objectUrl = URL.createObjectURL(response.data);
                setSrc(objectUrl);
            } catch (error) {
                console.error('Error loading image:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchImage();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [imageId]);

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center h-100"><Spinner size="sm" animation="border" /></div>;
    }

    if (!src) {
        return <div className="d-flex justify-content-center align-items-center h-100 text-muted">Ошибка</div>;
    }

    return (
        <img 
            src={src} 
            alt={alt} 
            style={{ objectFit: 'contain', maxHeight: '100%', maxWidth: '100%' }} 
        />
    );
};

const Images: React.FC = () => {
    const [images, setImages] = useState<AdminImageDetailDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Filter states
    const [filterDoctor, setFilterDoctor] = useState('');
    const [filterDateStart, setFilterDateStart] = useState('');
    const [filterDateEnd, setFilterDateEnd] = useState('');
    const [filterDiagnosis, setFilterDiagnosis] = useState('all');
    const [filterContour, setFilterContour] = useState('all');
    const [appliedFilters, setAppliedFilters] = useState<ImageFilters>(EMPTY_FILTERS);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState<ModalContentType>(null);
    const [modalContent, setModalContent] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Stage-image cleanup states
    const [showCleanupModal, setShowCleanupModal] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const [cleanupResult, setCleanupResult] = useState<StageImageCleanupResult | null>(null);
    const [cleanupError, setCleanupError] = useState<string | null>(null);

    const openCleanupModal = () => {
        setCleanupResult(null);
        setCleanupError(null);
        setShowCleanupModal(true);
    };

    const handleCleanup = async () => {
        setCleaning(true);
        setCleanupError(null);
        try {
            const response = await api.post<StageImageCleanupResult>('/admin/cleanup/stage-images');
            setCleanupResult(response.data);
        } catch (error) {
            console.error('Cleanup failed:', error);
            setCleanupError('Не удалось выполнить очистку. Попробуйте позже.');
        } finally {
            setCleaning(false);
        }
    };

    const handleShowModal = (type: ModalContentType, content: string) => {
        setModalType(type);
        setModalContent(content);
        setShowModal(true);
        setCopySuccess(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalType(null);
        setModalContent('');
        setCopySuccess(false);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(modalContent);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const doctor = filterDoctor.trim();
            setCurrentPage(1);
            setAppliedFilters((current) => current.doctor === doctor ? current : { ...current, doctor });
        }, 500);
        return () => window.clearTimeout(timer);
    }, [filterDoctor]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchImages = async () => {
            setLoading(true);
            try {
                const response = await api.get<PageDto<AdminImageDetailDto>>('/admin/images/detailed', {
                    signal: controller.signal,
                    params: {
                        page: currentPage - 1,
                        size: ITEMS_PER_PAGE,
                        doctor: appliedFilters.doctor || undefined,
                        dateStart: appliedFilters.dateStart || undefined,
                        dateEnd: appliedFilters.dateEnd || undefined,
                        diagnosis: appliedFilters.diagnosis,
                        contour: appliedFilters.contour,
                    },
                });
                setImages(response.data.content ?? []);
                setTotalPages(response.data.totalPages ?? 0);
                setTotalElements(response.data.totalElements ?? 0);
            } catch (error: any) {
                if (error?.code === 'ERR_CANCELED') return;
                console.error('Error fetching images:', error);
                setImages([]);
                setTotalPages(0);
                setTotalElements(0);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };

        fetchImages();
        return () => controller.abort();
    }, [currentPage, appliedFilters]);

    const applyFilters = () => {
        setCurrentPage(1);
        setAppliedFilters({
            doctor: filterDoctor.trim(),
            dateStart: filterDateStart,
            dateEnd: filterDateEnd,
            diagnosis: filterDiagnosis,
            contour: filterContour,
        });
    };

    const applyFilterValue = (key: 'dateStart' | 'dateEnd' | 'diagnosis' | 'contour', value: string) => {
        setCurrentPage(1);
        setAppliedFilters((current) => ({ ...current, [key]: value }));
    };

    const clearFilters = () => {
        setFilterDoctor('');
        setFilterDateStart('');
        setFilterDateEnd('');
        setFilterDiagnosis('all');
        setFilterContour('all');
        setCurrentPage(1);
        setAppliedFilters(EMPTY_FILTERS);
    };

    // Pagination display calculations
    const startIndex = totalElements === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalElements);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </Pagination.Item>
                );
            }
        } else {
            // Always show first page
            items.push(
                <Pagination.Item
                    key={1}
                    active={1 === currentPage}
                    onClick={() => handlePageChange(1)}
                >
                    1
                </Pagination.Item>
            );

            // Show ellipsis if needed
            if (currentPage > 3) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            
            for (let i = start; i <= end; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </Pagination.Item>
                );
            }

            // Show ellipsis if needed
            if (currentPage < totalPages - 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }

            // Always show last page
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }
        
        return items;
    };

    const handleDownload = async (imageId: string) => {
        try {
            const response = await api.get(`/images/file/${imageId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `image-${imageId}.jpg`); // Default extension/name
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Не удалось скачать файл');
        }
    };

    return (
        <Container className="py-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold text-primary mb-0">Детальные Снимки</h2>
                <div className="d-flex align-items-center gap-3">
                    <Button
                        variant="outline-danger"
                        className="rounded-pill"
                        onClick={openCleanupModal}
                    >
                        <i className="bi bi-trash3 me-1"></i>Очистить временные снимки
                    </Button>
                    <Badge bg="light" text="dark" className="fs-6 border">
                        Всего: {totalElements}
                    </Badge>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-5" style={{ borderRadius: '15px' }}>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={3}>
                            <label className="form-label small text-muted text-uppercase fw-bold">Поиск врача</label>
                            <input
                                type="text"
                                className="form-control bg-light border-0"
                                placeholder="Имя врача..."
                                value={filterDoctor}
                                onChange={(e) => setFilterDoctor(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                            />
                        </Col>
                        <Col md={3}>
                            <label className="form-label small text-muted text-uppercase fw-bold">Диагноз</label>
                            <select
                                className="form-select bg-light border-0"
                                value={filterDiagnosis}
                                onChange={(e) => {
                                    setFilterDiagnosis(e.target.value);
                                    applyFilterValue('diagnosis', e.target.value);
                                }}
                            >
                                <option value="all">Все</option>
                                <option value="has_diagnosis">Есть диагноз</option>
                                <option value="no_diagnosis">Нет диагноза</option>
                                <option value="success">Успешный диагноз</option>
                                <option value="error">Ошибка диагноза</option>
                            </select>
                        </Col>
                        <Col md={3}>
                            <label className="form-label small text-muted text-uppercase fw-bold">Контур</label>
                            <select
                                className="form-select bg-light border-0"
                                value={filterContour}
                                onChange={(e) => {
                                    setFilterContour(e.target.value);
                                    applyFilterValue('contour', e.target.value);
                                }}
                            >
                                <option value="all">Все</option>
                                <option value="auto">Авто контур</option>
                                <option value="manual">Ручной контур</option>
                                <option value="none">Без контура</option>
                            </select>
                        </Col>
                        <Col md={3}>
                            <label className="form-label small text-muted text-uppercase fw-bold">Период</label>
                            <div className="d-flex gap-2">
                                <input
                                    type="date"
                                    className="form-control bg-light border-0"
                                    value={filterDateStart}
                                    onChange={(e) => {
                                        setFilterDateStart(e.target.value);
                                        applyFilterValue('dateStart', e.target.value);
                                    }}
                                />
                                <input
                                    type="date"
                                    className="form-control bg-light border-0"
                                    value={filterDateEnd}
                                    onChange={(e) => {
                                        setFilterDateEnd(e.target.value);
                                        applyFilterValue('dateEnd', e.target.value);
                                    }}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                            Сбросить
                        </Button>
                        <Button variant="primary" onClick={applyFilters} disabled={loading}>
                            {loading ? <Spinner size="sm" animation="border" /> : 'Применить'}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {loading ? (
                <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
            ) : <Row xs={1} md={2} lg={3} xl={4} className="g-4">
                {images.length > 0 ? (
                    images.map((img) => (
                        <Col key={img.imageId}>
                            <Card className="h-100 border-0 shadow-sm hover-shadow transition-all" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                                <div style={{ height: '220px', position: 'relative', overflow: 'hidden', background: '#f1f5f9' }}>
                                    <ProtectedImage imageId={img.imageId} alt={img.bodyPart || 'Снимок'} />
                                    <div className="position-absolute top-0 end-0 p-2">
                                        <Badge bg="light" text="dark" className="shadow-sm border">
                                            {img.uploadDate ? new Date(img.uploadDate).toLocaleDateString('ru-RU') : 'N/A'}
                                        </Badge>
                                    </div>
                                </div>
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h5 className="card-title fw-bold mb-0 text-truncate" title={img.bodyPart}>{img.bodyPart || 'Неизвестная часть'}</h5>
                                    </div>
                                    <p className="card-text small text-muted mb-3">
                                        <span className="d-block">
                                            <i className="bi bi-person-fill me-1"></i>{img.doctorName}
                                        </span>
                                        <span className="d-block mt-1">
                                            <i className="bi bi-calendar3 me-1"></i>Дата снимка: {formatImageDate(img.uploadDate)}
                                        </span>
                                    </p>
                                    
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                        {img.aiDiagnosis ? (
                                            <Badge
                                                bg={img.diagnosisStatus === 'FAILED' ? 'danger' : 'success-subtle'}
                                                text={img.diagnosisStatus === 'FAILED' ? 'light' : 'success'}
                                                className="border border-success-subtle text-wrap text-start"
                                                style={{ maxWidth: '100%', whiteSpace: 'normal' }}
                                                title={getDiagnosisPreview(img.aiDiagnosis)}
                                            >
                                                {truncate(getDiagnosisPreview(img.aiDiagnosis))}
                                            </Badge>
                                        ) : (
                                            <Badge bg="secondary-subtle" text="secondary" className="border">Нет диагноза</Badge>
                                        )}
                                        
                                        {img.manualContourEngaged ? (
                                            <Badge bg="info-subtle" text="info" className="border border-info-subtle">Ручной контур</Badge>
                                        ) : img.contourDefined ? (
                                            <Badge bg="primary-subtle" text="primary" className="border border-primary-subtle">Авто контур</Badge>
                                        ) : null}
                                    </div>
                                </Card.Body>
                                <Card.Footer className="bg-white border-top-0 p-3 pt-0">
                                    <div className="d-flex flex-wrap gap-2">
                                        {img.aiDiagnosis && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="flex-fill rounded-pill"
                                                onClick={() => handleShowModal('diagnosis', img.aiDiagnosis || '')}
                                            >
                                                <i className="bi bi-clipboard-pulse me-1"></i>Диагноз
                                            </Button>
                                        )}
                                        {img.aiPrompt && (
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                className="flex-fill rounded-pill"
                                                onClick={() => handleShowModal('prompt', img.aiPrompt || '')}
                                            >
                                                <i className="bi bi-chat-text me-1"></i>Промпт
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="flex-fill rounded-pill"
                                            onClick={() => handleDownload(img.imageId)}
                                        >
                                            <i className="bi bi-download me-1"></i>Скачать
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))
                ) : (
                    <Col xs={12}>
                        <div className="text-center py-5 text-muted">
                            <h4>Ничего не найдено</h4>
                            <p>Попробуйте изменить параметры фильтрации</p>
                        </div>
                    </Col>
                )}
            </Row>}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-5 gap-3">
                    <span className="text-muted small">
                        Показано {startIndex}-{endIndex} из {totalElements}
                    </span>
                    <Pagination className="mb-0">
                        <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        />
                        {getPaginationItems()}
                        <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </div>
            )}

            {/* Modal for Diagnosis/Prompt */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        {modalType === 'diagnosis' ? (
                            <><i className="bi bi-clipboard-pulse me-2 text-success"></i>AI Диагноз</>
                        ) : (
                            <><i className="bi bi-chat-text me-2 text-info"></i>AI Промпт</>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <div
                        className="bg-light rounded p-3"
                        style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            fontFamily: modalType === 'prompt' ? 'monospace' : 'inherit',
                            fontSize: modalType === 'prompt' ? '0.85rem' : '1rem'
                        }}
                    >
                        {modalContent}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Закрыть
                    </Button>
                    <Button
                        variant={copySuccess ? 'success' : 'primary'}
                        onClick={handleCopy}
                    >
                        {copySuccess ? (
                            <><i className="bi bi-check-lg me-1"></i>Скопировано!</>
                        ) : (
                            <><i className="bi bi-clipboard me-1"></i>Копировать</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Stage-image cleanup confirm / result modal */}
            <Modal
                show={showCleanupModal}
                onHide={() => { if (!cleaning) setShowCleanupModal(false); }}
                centered
                backdrop={cleaning ? 'static' : true}
            >
                <Modal.Header closeButton={!cleaning} className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        <i className="bi bi-trash3 me-2 text-danger"></i>Очистка временных снимков
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {cleanupResult ? (
                        <div>
                            <p className="mb-3">
                                <i className="bi bi-check-circle-fill text-success me-2"></i>Очистка завершена.
                            </p>
                            <ul className="mb-0">
                                <li>Обработано отчётов с PDF: <strong>{cleanupResult.reportedAnalyses}</strong></li>
                                <li>Удалено временных снимков: <strong>{cleanupResult.imagesDeleted}</strong></li>
                                <li>Анализов не найдено: <strong>{cleanupResult.analysesMissing}</strong></li>
                            </ul>
                        </div>
                    ) : (
                        <>
                            <p className="mb-2">
                                Будут удалены промежуточные снимки стадий анализа (контур, асимметрия, граница, цвет)
                                для анализов, по которым уже сформирован PDF‑отчёт.
                            </p>
                            <p className="text-muted small mb-0">
                                Клинические и обзорные снимки, а также превью отчётов не затрагиваются. Действие необратимо.
                            </p>
                        </>
                    )}
                    {cleanupError && (
                        <div className="text-danger mt-3">
                            <i className="bi bi-exclamation-triangle me-1"></i>{cleanupError}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    {cleanupResult ? (
                        <Button variant="secondary" onClick={() => setShowCleanupModal(false)}>
                            Закрыть
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="secondary"
                                onClick={() => setShowCleanupModal(false)}
                                disabled={cleaning}
                            >
                                Отмена
                            </Button>
                            <Button variant="danger" onClick={handleCleanup} disabled={cleaning}>
                                {cleaning ? (
                                    <><Spinner as="span" size="sm" animation="border" className="me-2" />Очистка...</>
                                ) : (
                                    <><i className="bi bi-trash3 me-1"></i>Очистить</>
                                )}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default Images;
